import { api } from '../api/client';

// Barcode scheme (spec §12, mockup scanGo): ASP- part · ASS- stock item · ASL- location · AST- tag · ASD-{TYPE}-{id} document · ASU- user badge.
const DOC_ROUTES: Record<string, string> = {
  RV: 'rcv/rv',
  DN: 'mro/dn',
  PO: 'proc/po',
  RFQ: 'proc/rfq',
  RRFQ: 'mro/rfq',
  LOAN: 'loan/issue',
  SIN: 'inv/issue',
  RTN: 'inv/return',
};

/** The prefixes the scheme defines; anything else typed into the scan box is treated as a raw P/N, S/N or lot. */
export const SCAN_PREFIXES = ['ASP', 'ASS', 'ASL', 'AST', 'ASD', 'ASU'] as const;

export const hasScanPrefix = (code: string): boolean => SCAN_PREFIXES.some((p) => code.trim().toUpperCase().startsWith(`${p}-`));

/**
 * Client-side prefix routing — the fallback for the codes the API cannot resolve on its own yet
 * (tags live behind their QR deep link, documents are numbered per module). Returns null when only the API can answer.
 */
export function prefixRoute(code: string): string | null {
  const c = code.trim().toUpperCase();
  if (c.startsWith('AST-')) return `/t/${encodeURIComponent(c.slice(4))}`;
  if (c.startsWith('ASD-')) {
    const [, type = '', id = ''] = c.split('-');
    const base = DOC_ROUTES[type];
    return base && id ? `/${base}/${encodeURIComponent(id)}` : null;
  }
  return null;
}

/** What a scan resolved to: the route to open plus what the API recognised (shown in the toast and on inv/lookup). */
export interface ScanResult {
  code: string;
  kind: string;
  id: number | null;
  route: string;
  summary: string;
  found: boolean;
}

/**
 * Resolves any scanned or typed code. `GET /barcode/resolve` answers first — it owns the mapping and returns
 * `/inv/item/{id}` for ASS- codes and bare serials or lots, `/mdm/part/{id}` for ASP- codes and raw part numbers,
 * `/mdm/locations?loc={id}` for ASL- codes — and the prefix table catches what it cannot resolve (AST- tags, ASD- documents).
 * Anything still unresolved (an ASU- badge with no owner, a code that is not in stock) lands on the lookup screen.
 */
export async function resolveScanResult(code: string): Promise<ScanResult> {
  const c = code.trim().toUpperCase();
  const lookup = (summary: string): ScanResult => ({ code: c, kind: 'Unknown', id: null, route: c ? `/inv/lookup?q=${encodeURIComponent(c)}` : '/inv/lookup', summary, found: false });
  if (!c) return lookup('');
  try {
    const r = await api.GET('/api/v1/barcode/resolve', { params: { query: { code: c } } });
    const hit = r.data;
    if (hit?.found && hit.route) return { code: c, kind: hit.kind, id: hit.id, route: hit.route, summary: hit.summary, found: true };
    const byPrefix = prefixRoute(c);
    if (byPrefix) return { code: c, kind: c.startsWith('AST-') ? 'Tag' : 'Document', id: null, route: byPrefix, summary: hit?.summary ?? c, found: true };
    return lookup(hit?.summary ?? c);
  } catch {
    // offline or unauthorised — the prefix table still knows where tags and documents live
    const byPrefix = prefixRoute(c);
    return byPrefix ? { code: c, kind: 'Unknown', id: null, route: byPrefix, summary: c, found: true } : lookup(c);
  }
}

/** The route only — what the scan box and the wedge listener navigate to. */
export const resolveScan = async (code: string): Promise<string> => (await resolveScanResult(code)).route;

/**
 * Scan-mode ownership (WO-13). A `/scan` flow listens for the wedge itself and decides what the code means inside that
 * flow — an item into the cart, a bin as the destination — so the app-wide handler must stand down while one is on
 * screen, or a single scan would both fill the cart and navigate away to the item card. The flow claims ownership while
 * it is mounted and releases it on unmount; the count is a module value because both sides are outside React state.
 */
let scanOwners = 0;

export function claimScanInput(): () => void {
  scanOwners++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    scanOwners--;
  };
}

/** True while a scan flow owns the wedge — `useGlobalScan` ignores every burst until it lets go. */
export const scanInputOwned = (): boolean => scanOwners > 0;

export interface WedgeOptions {
  /** minimum number of characters for a burst to count as a scan */
  minLength: number;
  /** maximum gap between two keystrokes of the same burst, in ms */
  maxGapMs: number;
}

export const WEDGE_DEFAULTS: WedgeOptions = { minLength: 6, maxGapMs: 100 };

interface KeyLike {
  key: string;
  preventDefault: () => void;
}

/**
 * Keyboard-wedge burst detection (spec §12): ≥ minLength printable characters arriving within maxGapMs of each other,
 * terminated by Enter. Returns a keydown handler; `now` is injectable for tests.
 */
export function createWedgeDetector(onCode: (code: string) => void, opts: WedgeOptions = WEDGE_DEFAULTS) {
  let buf = '';
  let last = 0;
  return (e: KeyLike, now: number = performance.now()): void => {
    if (now - last > opts.maxGapMs) buf = '';
    last = now;
    if (e.key === 'Enter') {
      const code = buf.trim().toUpperCase();
      buf = '';
      if (code.length >= opts.minLength) {
        e.preventDefault();
        onCode(code);
      }
      return;
    }
    if (e.key.length === 1) buf += e.key;
  };
}
